import dico from "./language/dico.js";
import { formatRecordTitleWithAge } from "./record-util.js";
import { NiveauxModel, RecordModel } from "./types.js";

/**
 * Athlete identity used to match records and minima of a category.
 */
export type CategoryTooltipAthlete = {
  catPoidId: number | null;
  catAgeId: number | null;
  disciplineId: number | null;
};

/** Canonical lift order inside each organisation block (OPL-style). */
const MOVEMENT_ORDER = ["squat", "bench", "deadlift", "total"] as const;

/**
 * Capitalizes the first letter of a movement name for the tooltip line.
 */
const formatMovementLabel = (mouvement: string | null | undefined): string => {
  const fallback = dico().speaker.noData;
  if (!mouvement) {
    return fallback;
  }
  return mouvement.charAt(0).toUpperCase() + mouvement.slice(1);
};

/**
 * Formats a weight with the localized kg unit.
 */
const formatKg = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return dico().speaker.noData;
  }
  return `${value}${dico().common.kg}`;
};

/**
 * Sort key so squat / bench / deadlift / Total stay in a stable order.
 */
const movementSortKey = (mouvement: string | null | undefined): number => {
  const index = MOVEMENT_ORDER.indexOf(
    (mouvement || "").toLowerCase() as (typeof MOVEMENT_ORDER)[number],
  );
  return index === -1 ? MOVEMENT_ORDER.length : index;
};

/**
 * Formats one record line for the category tooltip (movement / weight / holder).
 */
const formatRecordLine = (record: RecordModel, noData: string): string => {
  const athleteName = record.athleteName || noData;
  const mouvement = formatMovementLabel(record.mouvement);
  const poids = formatKg(record.poids);
  const dateLieu = record.dateLieu || noData;
  return `${mouvement}: ${poids} - ${athleteName} (${dateLieu})`;
};

/**
 * Groups category records by organisation, with title headers and blank lines.
 * Header is `Record de France · SNR` (recordLabel preferred, age code appended).
 *
 * Encounter order of organisations is preserved (typically sync order).
 *
 * @param records - Records already filtered to the athlete category.
 * @param noData - Localized placeholder for missing fields.
 * @param organisationFallback - Localized label when org name is missing.
 * @returns Multiline string, or empty when there are no records.
 */
const formatRecordsGroupedByOrganisation = (
  records: RecordModel[],
  noData: string,
  organisationFallback: string,
): string => {
  const groups: {
    key: string;
    name: string;
    records: RecordModel[];
  }[] = [];
  for (const record of records) {
    const key =
      record.organisationId?.trim() || record.organisationName || "__legacy__";
    const existing = groups.find((group) => group.key === key);
    if (existing) {
      existing.records.push(record);
      continue;
    }
    // Prefer federal record label + age (e.g. "Record de France · SNR")
    groups.push({
      key,
      name: formatRecordTitleWithAge(record, organisationFallback),
      records: [record],
    });
  }
  return groups
    .map((group) => {
      const lines = [...group.records]
        .sort(
          (a, b) => movementSortKey(a.mouvement) - movementSortKey(b.mouvement),
        )
        .map((record) => formatRecordLine(record, noData));
      return `${group.name}\n${lines.join("\n")}`;
    })
    .join("\n\n");
};

/**
 * Builds the multiline tooltip for a category: records then minima (niveaux).
 *
 * Records are grouped by organisation (name header + blank line between blocks).
 * Same content as the Electron group grid category cell.
 *
 * @param athlete - Athlete whose weight/age/discipline category is shown.
 * @param records - All meet records (filtered to the athlete category).
 * @param niveauxList - All minima levels (filtered to the athlete category).
 * @returns Tooltip text, or an empty string when the category has neither.
 */
export const formatCategoryRecordsLevelsTooltip = (
  athlete: CategoryTooltipAthlete,
  records: RecordModel[],
  niveauxList: NiveauxModel[],
): string => {
  const myDico = dico();
  const noData = myDico.speaker.noData;
  const organisationFallback =
    myDico.admin.records.displayModal.organisationFallback;
  const recordsCategory = records.filter(
    (record) =>
      record.catPoidId === athlete.catPoidId &&
      record.catAgeId === athlete.catAgeId &&
      record.disciplineId === athlete.disciplineId,
  );
  const niveauxCategory = niveauxList.filter(
    (niveau) =>
      niveau.catPoidId === athlete.catPoidId &&
      niveau.catAgeId === athlete.catAgeId &&
      niveau.disciplineId === athlete.disciplineId,
  );
  const recordsString = formatRecordsGroupedByOrganisation(
    recordsCategory,
    noData,
    organisationFallback,
  );
  const niveauxString = niveauxCategory
    .map((niveau) => `${niveau.nom || noData}: ${formatKg(niveau.totalMin)}`)
    .join("\n");
  return [
    recordsString && `${myDico.admin.records.title}:\n${recordsString}`,
    niveauxString && `${myDico.admin.niveaux.title}:\n${niveauxString}`,
  ]
    .filter(Boolean)
    .join("\n\n");
};
